import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CardActions, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Form, FormRenderProps } from 'react-final-form';

import { Button, Divider, Card, CardContent } from '@occc/ui-design-system';
import { Container, PageHeader, useAppLayoutContextConsumer } from '@occc-app/design-system';
import { SteeringWalletBasis, Unit_Of_Measure } from '@occc/graphql';
import { AwardType } from '@occc-app/offerHelpers/types';

import { WalletFormContent } from './WalletFormContent';
import { breadcrumbLinks } from './constants';

import { WalletFormValues } from '../types';
import { validateWallet } from '../validation';

const PAGE_TITLE = 'Create Wallet';

interface WalletCreatePageProps {
  onSubmit: (value: WalletFormValues) => void;
}

const initialValues: Partial<WalletFormValues> = {
  countWalletBy: Unit_Of_Measure.Unit,
  period: SteeringWalletBasis.Monthly,
  incentivizeResponses: {
    isEnabled: true,
    awardType: AwardType.AwardPoints,
    awardPoints: undefined,
    awardVouchers: undefined,
  },
};

export const CreateWalletPage: React.FC<WalletCreatePageProps> = ({ onSubmit }) => {
  const navigate = useNavigate();

  useAppLayoutContextConsumer(breadcrumbLinks);

  // TODO: move Container and PageHeader to level up and replace it along with Helmet by Page component
  const renderForm = React.useCallback(
    ({ handleSubmit, values, valid, submitting }: FormRenderProps<WalletFormValues>) => (
      <form onSubmit={handleSubmit}>
        <Container variant='md' includesPageHeader>
          <PageHeader
            title='Create a New Wallet'
            subtitle="Learn about your member's Wallet Capacity and understand Wallet Opportunity"
          />
          <Card>
            <CardContent>
              <Box>
                <WalletFormContent values={values} />
              </Box>
            </CardContent>

            <Divider />

            <CardActions>
              <Button role='button' onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button
                variant='contained'
                color='primary'
                type='submit'
                disabled={!valid || submitting}
              >
                Save
              </Button>
            </CardActions>
          </Card>
        </Container>
      </form>
    ),
    [navigate],
  );

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
      </Helmet>

      <Form<WalletFormValues>
        onSubmit={onSubmit}
        render={renderForm}
        validate={validateWallet}
        initialValues={initialValues}
      />
    </>
  );
};
